import { Component, computed, inject, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { translateSignal, TranslocoService } from '@jsverse/transloco';
import { VxButtonComponent } from '@ui/button/button.component';
import { SharedModule } from 'src/app/core/shared.module';
import { VButtonV2Component } from '../../ui-kit/buttons/button-v2/button.component';
import { VKratosUiAppearance, VKratosUiButtonWidget } from '../types';

/** Actions that detach a credential — rendered in the danger tone. */
const UNLINK_ACTIONS: readonly string[] = ['totp_unlink', 'webauthn_remove'];

@Component({
  selector: 'v-kratos-button',
  imports: [
    ReactiveFormsModule,
    SharedModule,
    VButtonV2Component,
    VxButtonComponent,
  ],
  template: `
    @let i = widget();
    @let attrs = i.attributes;

    @if (appearance() === 'vx') {
      <vx-button
        [type]="attrs.type === 'submit' ? 'submit' : 'button'"
        [variant]="isUnlink() ? 'outline' : 'solid'"
        [tone]="isUnlink() ? 'danger' : 'primary'"
        [disabled]="busy()"
        (click)="onPress($event)">
        {{ label() }}
      </vx-button>
    } @else {
      <v-button-v2
        [id]="i.id"
        [type]="attrs.type === 'submit' ? 'submit' : 'button'"
        [variant]="attrs.name === 'provider' ? 'outline' : 'primary'"
        [formGroup]="formGroup()"
        [fullWidth]="true"
        [disabled]="busy()"
        (click)="onPress($event)">
        <div class="flex gap-4 items-center justify-center select-none">
          @if (attrs.name === 'provider') {
            @switch (attrs.value) {
              @case ('google') {
                <img
                  src="assets/images/icons/google.svg"
                  alt="Google"
                  class="h-5 min-w-5" />
              }
              @case ('github') {
                <img
                  src="assets/images/icons/github.svg"
                  alt="GitHub"
                  class="min-w-5 h-5" />
              }
              @case ('microsoft') {
                <img
                  src="assets/images/icons/microsoft.svg"
                  alt="Microsoft"
                  class="min-w-5 h-5" />
              }
              @case ('apple') {
                <img
                  src="assets/images/icons/apple.svg"
                  alt="Apple"
                  class="min-w-5 h-5" />
              }
            }

            <span class="hidden md:block">
              {{ label() }}
            </span>
          } @else {
            {{ label() }}
          }
        </div>
      </v-button-v2>
    }
  `,
})
export class VKratosButtonComponent {
  widget = input.required<VKratosUiButtonWidget>();
  formGroup = input.required<FormGroup>();
  appearance = input<VKratosUiAppearance>('default');
  busy = input(false);

  isUnlink = computed(() =>
    UNLINK_ACTIONS.includes(this.widget().attributes.name)
  );

  translate = inject(TranslocoService);

  pressed = output<{
    event: Event;
    widget: VKratosUiButtonWidget;
  }>();

  onPress(event: Event) {
    this.pressed.emit({
      event: event,
      widget: this.widget(),
    });
  }

  private translationKey = computed(() => {
    const label = this.widget().label;

    if (!label) {
      return '';
    }

    // oidc buttons (labels for sign up and sign in)
    if (label.id === 1040002 || label.id === 1010002) {
      const provider = (label.context as { provider: string }).provider;
      return this.translate.translate(
        `kratosMessages.oidcProviders.${provider}`
      );
    }

    return this.translate.translate(
      `kratosMessages.${label?.id}`,
      label?.context
    );
  });

  label = translateSignal(this.translationKey);
}
